import { FunctionComponent, useEffect } from 'react';
import { useAppInsightsContext } from '@microsoft/applicationinsights-react-js';
import languages from '../../Data/languages';
import ColorSchemes from '../../Data/ColorSchemes.json';
import { NextButton } from '../NextButton';
import { parseHtml } from '../Helper';
import { CheckboxRadioInput } from '../CheckboxRadioInput';
import type { LegacyResponse, Template } from '../../types';

interface MultipleChoiceQuestion {
  question: string;
  choices?: string[];
  allowMultipleAnswers?: boolean;
}

type FormattedQuestion = string | MultipleChoiceQuestion;

interface FollowUpQuestion {
  question: FormattedQuestion;
  enableRating?: boolean;
}

interface FollowUpQuestionsPageProps {
  ratings?: number[];
  template: Template;
  response: LegacyResponse;
  updateResponse: (key: string, value: unknown) => void;
  setMultipleChoice: (
    e: React.MouseEvent<HTMLInputElement>,
    answerKey: string,
    choice: string
  ) => void;
  toggleNextSection: () => void;
  sendFinalResponse: (onSuccess?: () => void) => void;
  sending?: boolean;
  questionFirst?: boolean;
  togglePreviousSection: () => void;
  allowPreviousButton?: boolean;
}

function formatQuestionIfJson(question: string): FormattedQuestion {
  try {
    const res = JSON.parse(question);
    return res;
  } catch {
    return question;
  }
}

function getRatingBackgroundColor(
  rating: number,
  isSelected: boolean,
  template: Template
): string | undefined {
  if (isSelected) {
    return '';
  }
  if (template.htmlTemplateId === 1) {
    return (
      (rating < 7 && ColorSchemes.DETRACTOR) ||
      ((rating === 7 || rating === 8) && ColorSchemes.PASSIVE) ||
      (rating > 8 && ColorSchemes.PROMOTER) ||
      undefined
    );
  }
  return template.colorScheme;
}

export const FollowUpQuestionsPage: FunctionComponent<FollowUpQuestionsPageProps> = ({
  ratings,
  template,
  response,
  updateResponse,
  setMultipleChoice,
  toggleNextSection,
  sendFinalResponse,
  sending,
  questionFirst,
  togglePreviousSection,
  allowPreviousButton
}) => {
  const appInsights = useAppInsightsContext();
  useEffect(() => {
    appInsights.trackPageView({ name: 'FollowUpQuestionsPage.tsx' });
  }, [appInsights]);
  const language = template.language.name;

  function getNextButtonLabel() {
    if (questionFirst) {
      return languages.NEXT[language];
    }
    if (sending) {
      return languages.SUBMITTING[language];
    }
    return languages.SUBMIT[language];
  }

  return (
    <div className="body">
      {template.enableFurtherQuestionsHeaderText && (
        <div className="furtherQuestionsHeaderText">
          {parseHtml(template.enableFurtherQuestionsHeaderText)}
        </div>
      )}
      <div id="furtherQuestionsWrapper">
        {(
          [
            template.question1
              ? {
                  question: formatQuestionIfJson(template.question1 as string),
                  enableRating: template.enableRatingForQuestion1 as boolean | undefined
                }
              : null,
            template.question2
              ? {
                  question: formatQuestionIfJson(template.question2 as string),
                  enableRating: template.enableRatingForQuestion2 as boolean | undefined
                }
              : null,
            template.question3
              ? {
                  question: formatQuestionIfJson(template.question3 as string),
                  enableRating: template.enableRatingForQuestion3 as boolean | undefined
                }
              : null,
            template.question4
              ? {
                  question: formatQuestionIfJson(template.question4 as string),
                  enableRating: template.enableRatingForQuestion4 as boolean | undefined
                }
              : null,
            template.question5
              ? {
                  question: formatQuestionIfJson(template.question5 as string),
                  enableRating: template.enableRatingForQuestion5 as boolean | undefined
                }
              : null
          ] as Array<FollowUpQuestion | null>
        ).map((q, key) => {
          const answer = 'answer' + (key + 1);
          if (!q) {
            return '';
          }

          if (typeof q.question === 'object') {
            //multiple choice
            const multipleChoiceQuestion = q.question;
            const inputType = multipleChoiceQuestion.allowMultipleAnswers ? 'checkbox' : 'radio';
            const inputNamePrefix = multipleChoiceQuestion.allowMultipleAnswers
              ? 'checkbox'
              : 'choice';
            return (
              <div key={answer}>
                <div className="extra-question">{multipleChoiceQuestion.question}</div>
                <div className="extra-question-choices">
                  {multipleChoiceQuestion.choices?.map((c: string, index: number) => {
                    let existingAnswer: { answer?: string[] } | string[];

                    try {
                      existingAnswer = JSON.parse((response[answer] as string | undefined) ?? '[]');
                    } catch {
                      existingAnswer = [];
                    }

                    const alreadyAnswered =
                      existingAnswer && !Array.isArray(existingAnswer) && existingAnswer.answer
                        ? existingAnswer.answer.includes(c)
                        : false;

                    return (
                      <CheckboxRadioInput
                        key={c}
                        type={inputType}
                        question={c}
                        name={inputNamePrefix + key}
                        index={index}
                        onClick={(e) => {
                          setMultipleChoice(e, answer, c);
                        }}
                        defaultChecked={alreadyAnswered}
                      />
                    ); //title
                  })}
                </div>
              </div>
            );
          }

          if (q.enableRating) {
            //rating
            const questionText = q.question;
            return (
              <div key={answer}>
                <div className="extra-question">{questionText}</div>
                <div className="rating">
                  {ratings?.map((r: number) => {
                    const currentAnswer = Number.parseInt(response[answer] as string);
                    const isSelected = r === currentAnswer;
                    const selectRating = () => {
                      updateResponse(answer, r + '');
                    };
                    return (
                      <div key={r}>
                        <button
                          type="button"
                          className="button-link"
                          onClick={selectRating}
                          style={{
                            backgroundColor: getRatingBackgroundColor(r, isSelected, template),
                            borderColor: isSelected ? template.colorScheme : ''
                          }}
                        >
                          <p
                            style={{ color: isSelected ? template.colorScheme : '' }}
                            className="rating-figure"
                          >
                            {r}
                          </p>
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          }

          //text
          const textQuestion = q.question;
          return (
            <div key={answer}>
              <div className="extra-question">{textQuestion}</div>
              <textarea
                onChange={(e) => updateResponse(answer, e.target.value)}
                rows={2}
                value={response[answer] as string | undefined}
              ></textarea>
            </div>
          );
        })}
      </div>
      <br />

      <div style={{ display: 'inline-block', width: '100%' }}>
        {allowPreviousButton && (
          <div className="clearfix" style={{ float: 'left' }}>
            <button
              type="submit"
              className="pull-right btn"
              onClick={() => {
                appInsights.trackEvent({
                  name: 'Back to comment page',
                  properties: response
                });
                togglePreviousSection();
              }}
              style={{ background: template.colorScheme }}
              disabled={sending}
            >
              {languages.PREVIOUS[language]}
            </button>
          </div>
        )}
        <div className="clearfix" style={{ float: 'right' }}>
          <NextButton
            className="pull-right btn"
            onClick={() => {
              appInsights.trackEvent({ name: 'More Answers', properties: response });
              template.questionFirst ? toggleNextSection() : sendFinalResponse();
            }}
            style={{ background: template.colorScheme }}
            disabled={sending}
            loading={sending}
          >
            {getNextButtonLabel()}
          </NextButton>
        </div>
      </div>
    </div>
  );
};
